import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { fetchAccounts, getAccounts, isEthereum, isMetaMask } from './utils';
import { ethers } from 'ethers';
import { emptyState, IState, Reducer } from './reducer';
import { useMoralis } from 'react-moralis';
import useNetworks, { Network, TestNetwork } from '../Networks/useNetworks';
import { redirectToChromeStore } from '../../utils/redirector';
import { Contracts, IContractConfig } from '../../contracts/types';
import useAsyncEffect from '../../utils/hooks/effects/async';
import { IParent } from '../../components/common/types';
import { useEffectOnce } from 'usehooks-ts';
import { igoConfig } from '../../contracts/igo/contract';
import { userProfileConfig } from '../../contracts/userProfile/contract';
import { createStandaloneToast } from '@chakra-ui/react';

interface IContext extends IState {
	login: () => void;
	logout: () => void;
}

const emptyContext: IContext = {
	...emptyState,
	login: () => null,
	logout: () => null,
};

const Context = createContext(emptyContext);

export const BlockchainProvider = ({ children }: IParent) => {
	/** @TODO: Deprecate */
	const [isEthereumCheckPending_, setIsEthereumCheckPending_] = useState(false);
	const [state, dispatch] = useReducer(Reducer, emptyContext);

	const { switchNetwork } = useNetworks();
	const { toast } = createStandaloneToast();
	const moralis = useMoralis();
	const contractConfig: { [p in Contracts]: IContractConfig } = useMemo(
		() => ({
			igo: igoConfig[state.chainId],
			userProfile: userProfileConfig[state.chainId],
		}),
		[state.chainId]
	);

	/**
	 * Logs into wallet provider, Moralis and the application.
	 */
	const login = useCallback(async () => {
		if (!state.isEthereum) {
			redirectToChromeStore();
			return;
		}

		if (!moralis.isAuthenticated) {
			await moralis.authenticate({ signingMessage: 'Log in using Moralis' });
		}

		if (state.isLoggedIn) {
			return;
		}

		const accounts = await fetchAccounts();

		dispatch({
			account: accounts[0],
			isLoggedIn: true,
		});
	}, [state.isEthereum, state.isLoggedIn, moralis.isAuthenticated]);

	/**
	 * Logs out of the wallet provider, Moralis and the application.
	 */
	const logout = useCallback(async () => {
		dispatch({
			isLoggedIn: emptyContext.isLoggedIn,
			account: emptyContext.account,
		});

		await moralis.logout();
	}, []);

	useEffect(() => {}, [state.isLoggedIn]);

	/** Ethereum setup */
	useEffectOnce(() => {
		dispatch({ isEthereum: isEthereum() });
	});

	/** Contracts setup */
	useEffect(() => {
		if (!state.signer || !state.isChainValid) {
			return;
		}

		dispatch({
			contracts: {
				igo: new ethers.Contract(contractConfig.igo.address, contractConfig.igo.abi, state.signer),
				userProfile: new ethers.Contract(
					contractConfig.userProfile.address,
					contractConfig.userProfile.abi,
					state.signer
				),
			},
		});
	}, [state.signer, state.isChainValid]);

	/** MetaMask presence check */
	useEffect(() => {
		if (state.isEthereum) {
			dispatch({ isMetaMask: isMetaMask() });
		}
	}, [state.isEthereum]);

	/** Account check and retrieval */
	useAsyncEffect(async () => {
		if (state.isEthereum) {
			const accounts = await getAccounts();
			if (!accounts.length) {
				dispatch({
					isLoggedIn: emptyContext.isLoggedIn,
					account: emptyContext.account,
					isLoading: false,
				});
			} else {
				dispatch({
					account: accounts[0],
					isLoggedIn: true,
					isLoading: false,
				});
			}
		} else if (isEthereumCheckPending_) {
			dispatch({
				isLoggedIn: emptyContext.isLoggedIn,
				isLoading: false,
			});
			setIsEthereumCheckPending_(false);
		}

		if (!isEthereumCheckPending_) {
			setTimeout(() => {
				setIsEthereumCheckPending_(state.isLoading);
			}, 150);
		}
	}, [state.isEthereum, isEthereumCheckPending_]);

	/** Account listener */
	useEffect(() => {
		if (!state.isEthereum) {
			return;
		}

		const onAccountsChanged = (accounts: any) => {
			if (!accounts) {
				return;
			}

			if (accounts.length) {
				dispatch({
					account: accounts[0],
					isLoggedIn: true,
				});
			} else {
				void logout();
				window.location.assign('/');
			}
		};
		window.ethereum.on('accountsChanged', onAccountsChanged);
	}, [state.isEthereum]);

	/** Provider and signer retrieval */
	useEffect(() => {
		if (state.account === emptyContext.account) {
			dispatch({
				provider: emptyContext.provider,
				signer: emptyContext.signer,
			});

			return;
		}

		const provider = new ethers.providers.Web3Provider(window.ethereum);
		const signer = provider.getSigner();

		dispatch({
			provider: provider,
			signer: signer,
		});
	}, [state.account, state.chainId]);

	/** Network retrieval */
	useAsyncEffect(async () => {
		if (!state.isEthereum || !window.ethereum.request) {
			return;
		}

		const chainIdRaw = await window.ethereum.request({ method: 'eth_chainId' });
		dispatch({ chainId: parseInt(chainIdRaw, 16) });
	}, [state.isEthereum]);

	/** Network listener */
	useEffect(() => {
		if (!state.isEthereum) {
			return;
		}

		const onChainChanged = async (chainIdRaw: string) => {
			dispatch({ chainId: parseInt(chainIdRaw, 16) });
		};
		window.ethereum.on('chainChanged', onChainChanged);
	}, [state.isEthereum]);

	/** Network check */
	useEffect(() => {
		const supportedNetworkIds = Object.values(Network).filter(
			value => value !== Network._ && !Object.values(TestNetwork).includes(value as keyof typeof Network)
		);
		const isCurrentNetworkSupported = supportedNetworkIds.includes(state.chainId);

		dispatch({ isChainValid: isCurrentNetworkSupported });
	}, [state.chainId]);

	useEffectOnce(() => dispatch({ hasIssuedNetworkWarning: !!localStorage.getItem('hasIssuedNetworkWarning') }));

	/** Automatic network-change trigger */
	useEffect(() => {
		if (state.hasIssuedNetworkWarning || state.isChainValid) {
			return;
		}

		toast({ title: 'To use the app, please switch your network', status: 'info' });
		localStorage.setItem('hasIssuedNetworkWarning', 'yeeeeeeeeaaAAAHHHH!!@1');

		void switchNetwork();
	}, [state.isChainValid, state.hasIssuedNetworkWarning]);

	return (
		<Context.Provider
			value={{
				...state,
				login,
				logout,
			}}
		>
			{children}
		</Context.Provider>
	);
};

export const useBlockchainProvider = () => useContext(Context);
